import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { UserContext } from "../UserContext";
import { toast } from "react-toastify";
import Header from "../Header";
import apiClient from "../../api/client";
import {
  getRailtelPlans,
  getRailtelOperatorMapping,
  saveRailtelOperatorMapping,
} from "../../api/railtelApi";
import "./index.css";

/**
 * Same job as InternetPlanMapping — assign packages + a per-operator
 * price for each — but flat, since RailTel packages don't have the
 * plan -> sub-plan nesting Internet plans do. Each row here is already
 * a specific speed+duration combo (e.g. "20Mbps" / 3 months).
 */
const RailtelPlanMapping = () => {
  const { id } = useParams();
  const { user } = useContext(UserContext);

  const [operator, setOperator] = useState(null);
  const [plans, setPlans] = useState([]); // [{ id, plan_name, duration_months, base_price }]
  const [mappedPlans, setMappedPlans] = useState({}); // { [plan_id]: price }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkAmount, setBulkAmount] = useState("");

  const isAdmin = user?.role === "admin";

  const fetchOperator = async () => {
    try {
      const { data } = await apiClient.get(`/operator_api.php?id=${id}`);
      setOperator(data.operator || null);
    } catch (err) {
      toast.error("Failed to load operator");
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await getRailtelPlans();
      if (data.status === "success") {
        setPlans(data.plans || []);
      }
    } catch (err) {
      toast.error("Failed to load RailTel packages");
    }
  };

  const fetchOperatorMapping = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await getRailtelOperatorMapping(id);
      if (data.status === "success" && Array.isArray(data.mappings)) {
        const map = {};
        data.mappings.forEach((m) => {
          map[m.plan_id] = m.price;
        });
        setMappedPlans(map);
      } else {
        setMappedPlans({});
      }
    } catch (err) {
      toast.error("Failed to load operator mapping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchOperator();
  }, []);

  useEffect(() => {
    fetchOperatorMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const findPlan = (planId) => plans.find((p) => p.id === Number(planId));

  const togglePlan = (planId) => {
    setMappedPlans((prev) => {
      const current = { ...prev };
      if (current[planId] !== undefined) {
        delete current[planId];
      } else {
        const plan = findPlan(planId);
        let price = "";
        if (plan) {
          const base = parseFloat(plan.base_price);
          const bulk = bulkAmount && parseInt(bulkAmount) > 0 ? parseInt(bulkAmount) : 0;
          price = (base + bulk).toString();
        }
        current[planId] = price;
      }
      return current;
    });
  };

  const handlePriceChange = (planId, value) => {
    setMappedPlans((prev) => ({ ...prev, [planId]: value }));
  };

  const handleSelectAll = () => {
    const map = {};
    plans.forEach((plan) => {
      if (isAdmin) {
        const base = parseFloat(plan.base_price);
        const bulk = bulkAmount && parseInt(bulkAmount) > 0 ? parseInt(bulkAmount) : 0;
        map[plan.id] = (base + bulk).toString();
      } else {
        map[plan.id] = "";
      }
    });
    setMappedPlans(map);
  };

  const handleDeselectAll = () => {
    setMappedPlans({});
    setBulkAmount("");
  };

  const applyBulkAmount = () => {
    if (!bulkAmount || isNaN(parseInt(bulkAmount))) {
      toast.warn("Please enter a valid amount");
      return;
    }
    const amount = parseInt(bulkAmount);
    if (amount < 0) {
      toast.warn("Amount cannot be negative");
      return;
    }

    const selectedIds = Object.keys(mappedPlans);
    if (selectedIds.length === 0) {
      toast.warn("No packages selected");
      return;
    }

    const updated = { ...mappedPlans };
    selectedIds.forEach((planId) => {
      const plan = findPlan(planId);
      if (plan) {
        updated[planId] = (parseFloat(plan.base_price) + amount).toString();
      }
    });

    setMappedPlans(updated);
    toast.success(`Added ₹${amount} to ${selectedIds.length} package(s)`);
  };

  const allSelected = plans.length > 0 && Object.keys(mappedPlans).length === plans.length;

  const handleSaveMapping = async () => {
    const mappings = Object.keys(mappedPlans)
      .map((planId) => {
        const plan = findPlan(planId);
        const entered = mappedPlans[planId];
        const price =
          entered !== "" && entered !== null && entered !== undefined
            ? parseFloat(entered)
            : plan
              ? parseFloat(plan.base_price)
              : null;

        return { plan_id: parseInt(planId), price };
      })
      .filter((m) => m.price !== null && !isNaN(m.price));

    if (mappings.length === 0) {
      toast.warn("No packages selected");
      return;
    }

    setSaving(true);
    try {
      const { data } = await saveRailtelOperatorMapping(id, mappings);
      if (data.status === "success") {
        toast.success("RailTel package pricing saved successfully!");
        fetchOperatorMapping();
      } else {
        toast.error(data.message || "Failed to save mapping");
      }
    } catch (err) {
      toast.error("Server error while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header />

      <div className="railtel-mapping-container">
        <h1>
          RailTel Package Mapping{" "}
          {operator && <span>— {operator.operatorName}</span>}{" "}
          {isAdmin && <span className="admin-badge">(Admin)</span>}
        </h1>

        {loading ? (
          <p>Loading mapped packages...</p>
        ) : plans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🚄</div>
            <p>No RailTel packages configured yet.</p>
          </div>
        ) : (
          <>
            <div className="bulk-actions">
              <div className="bulk-selection">
                <div className="bulk-btn-group">
                  <button className="bulk-btn select-all-btn" onClick={handleSelectAll} disabled={allSelected}>
                    Select All
                  </button>
                  <button className="bulk-btn deselect-all-btn" onClick={handleDeselectAll}>
                    Deselect All
                  </button>
                </div>
                <span className="selected-count">
                  {Object.keys(mappedPlans).length} of {plans.length} packages selected
                </span>
              </div>

              {isAdmin && (
                <div className="bulk-amount-section">
                  <input
                    type="number"
                    placeholder="Bulk add amount"
                    value={bulkAmount}
                    onChange={(e) => setBulkAmount(e.target.value)}
                  />
                  <button className="bulk-btn" onClick={applyBulkAmount}>
                    Apply to Selected
                  </button>
                </div>
              )}
            </div>

            <div className="mapping-table-container">
              <table className="mapping-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>RailTel Package Name</th>
                    <th>Duration</th>
                    <th>Assign</th>
                    <th>Base Price</th>
                    <th>Custom Price</th>
                    <th>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => {
                    const isSelected = mappedPlans[plan.id] !== undefined;
                    const customPrice = isSelected ? mappedPlans[plan.id] : "";
                    const basePrice = parseFloat(plan.base_price);
                    const difference = customPrice ? parseFloat(customPrice) - basePrice : 0;

                    return (
                      <tr key={plan.id} className={isSelected ? "selected" : ""}>
                        <td>{plan.plan_name}</td>
                        <td className="railtel-package-name-cell">
                          {plan.railtel_package_name || <em>not set</em>}
                        </td>
                        <td>
                          {plan.duration_months} Month{plan.duration_months > 1 ? "s" : ""}
                        </td>
                        <td>
                          <input type="checkbox" checked={isSelected} onChange={() => togglePlan(plan.id)} />
                        </td>
                        <td>₹{basePrice}</td>
                        <td>
                          {isSelected ? (
                            <input
                              type="number"
                              placeholder="Enter custom price"
                              value={customPrice}
                              onChange={(e) => handlePriceChange(plan.id, e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {isSelected && customPrice ? (
                            <span className={`price-difference ${difference >= 0 ? "positive" : "negative"}`}>
                              {difference >= 0 ? "+" : ""}₹{difference}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mapping-actions">
              <button
                className="save-btn"
                onClick={handleSaveMapping}
                disabled={saving || Object.keys(mappedPlans).length === 0}
              >
                {saving ? "Saving..." : `Save Mapping (${Object.keys(mappedPlans).length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RailtelPlanMapping;
